import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { GlassCard } from '@/components/GlassCard';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, MapPin, Users, Loader2 } from 'lucide-react';
import { CATEGORY_META, getGeolocation } from '@/lib/utils';
import { EmptyState } from '@/components/Loaders';

export default function CategoryPage() {
  const { category } = useParams();
  const nav = useNavigate();
  const [venues, setVenues] = useState([]);
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = CATEGORY_META[category];

  useEffect(() => {
    (async () => {
      const c = await getGeolocation();
      setCoords(c);
      const params = { category };
      if (c) { params.lat = c.lat; params.lon = c.lon; }
      try {
        const r = await api.get('/venues', { params });
        setVenues(r.data);
      } finally {
        setLoading(false);
      }
    })();
  }, [category]);

  if (!meta) return <div className="p-8 text-white">Unknown category</div>;

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-30 bg-[#07070A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="mx-auto flex max-w-2xl items-center gap-2 px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => nav(-1)} className="text-white hover:bg-white/10" data-testid="category-back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-display text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }} data-testid="category-title">{meta.label}</h1>
          <div className="ml-auto"><Logo size={96} withText={false} /></div>
        </div>
      </header>
      <main className="mx-auto max-w-2xl space-y-3 px-4 py-4">
        <p className="text-sm text-white/70">
          {coords ? 'Sorted by distance from your location' : 'Popular nearby. Enable location for personalized results.'}
        </p>
        {loading ? (
          <div className="flex items-center gap-2 text-white/60"><Loader2 className="h-4 w-4 th-spin" /> Loading…</div>
        ) : venues.length === 0 ? (
          <EmptyState title="No Thirrsty spots here yet" body="Check the USA map for nearby cities." action={
            <Button onClick={() => nav('/map')} className="rounded-xl bg-[#F6B24A] text-[#1A1208] hover:bg-[#FFC065]">Open Map</Button>
          } />
        ) : (
          venues.map((v) => (
            <button
              key={v.id}
              onClick={() => nav(`/venue/${v.id}`)}
              className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-3 text-left transition-colors hover:border-white/20 th-float-in"
              data-testid={`category-venue-${v.id}`}
            >
              <img
                src={v.image_url}
                onError={(e) => { e.currentTarget.src = '/logo-hero.png'; }}
                alt={v.name} className="h-20 w-24 rounded-xl object-cover"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-white">{v.name}</p>
                  {v.is_21_plus && <Badge className="bg-white/10 text-white/70 border border-white/10">21+</Badge>}
                </div>
                <p className="text-xs text-white/60 truncate">{v.city}, {v.state}</p>
                <div className="mt-1 flex items-center gap-3 text-xs text-white/60">
                  {typeof v.distance_km === 'number' && <span>{(v.distance_km * 0.621371).toFixed(1)} mi</span>}
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" />{v.check_in_count || 0} check-ins</span>
                </div>
                {v.discount_text && (
                  <p className="mt-1 text-xs text-[#F6B24A] truncate"><MapPin className="inline h-3 w-3 mr-1" />{v.discount_text}</p>
                )}
              </div>
            </button>
          ))
        )}
      </main>
    </div>
  );
}
